import {IconButton} from '@wandb/components';
import * as globals from '@wandb/weave/common/css/globals.styles';
import {NameProps, pickNameProps} from '@wandb/weave/common/util/reactUtils';
import * as d3 from 'd3';
import _ from 'lodash';
import * as React from 'react';
import {useCallback, useMemo} from 'react';

import stepFilterHandleUrl from '../assets/step-filter-handle.svg';
import {clamp} from '../util/math';
import * as vec2 from '../util/vec2';

const INPUT_WIDTH = 267;
const MIN_NUMBER_OF_TICKS = 3;
const INPUT_CLASSNAME =
  'rounded-[5px] border border-moon-250 w-[75px] py-2 pl-4 h-full';
const HANDLE_WIDTH = 26; // width of the handle icon
const HANDLE_SIZE = 2; // width of the handle line
const HANDLE_HEIGHT = 18; // height of the handle icon
const HANDLE_Y_OFFSET = 8; // how much to overlap the handle icon with the scale line
const SCALE_THICKNESS = 16; // height of the selected range teal line
export const HEIGHT = 116; // full selectable axis height
const GAP_X = 24; // gap between the inputs and the axis

export const SelectableAxisInput = ({
  axisLabel,
  min,
  max,
  setMin,
  setMax,
  onClickReset,
  minPlaceholder,
  maxPlaceholder,
}: {
  axisLabel: string;
  min: string | undefined; // string to avoid issues as the user is typing in case they empty the input for example
  max: string | undefined;
  setMin: (min: string) => void;
  setMax: (max: string) => void;
  onClickReset: () => void;
  minPlaceholder: string;
  maxPlaceholder: string;
}) => {
  return (
    <div
      className="flex items-center text-[15px] text-moon-800"
      style={{width: INPUT_WIDTH}}>
      <div className="mr-12 w-[36px] font-semibold">{axisLabel}</div>
      <div className="mr-8 flex h-32 items-center gap-x-8 font-['Inconsolata']">
        <label htmlFor="min" hidden>
          {axisLabel} Min
        </label>
        <input
          id="min"
          type="number"
          placeholder={minPlaceholder}
          value={min ?? ''}
          className={INPUT_CLASSNAME}
          onChange={e => {
            setMin(e.target.value);
          }}
        />
        <div className="text-moon-500">to</div>
        <label htmlFor="max" hidden>
          {axisLabel} Max
        </label>
        <input
          id="max"
          type="number"
          placeholder={maxPlaceholder}
          value={max ?? ''}
          className={INPUT_CLASSNAME}
          onChange={e => {
            setMax(e.target.value);
          }}
        />
      </div>
      <IconButton
        onClick={onClickReset}
        size="md"
        variant="secondary"
        aria-label="Reset selected range"
        icon="reset"
      />
    </div>
  );
};

// Note I originally wrote this to support X, and Y Axises
// but decided to narrow the scope to just the X axis for time being
export const SelectableAxis = (
  props: {
    direction: 'top' | 'bottom' | 'left' | 'right';
    width: number;
    // A range of numbers(inclusive) that represents the current selection
    selection?: [number, number];
    ticks: number[];
    // On select takes a range and returns a new range.
    //
    // This allows the parent to recieve the control event
    // And response according setting a new range.
    onSelect: (range: [number, number]) => [number, number];
    onClear: () => void;
    axisLabel: string;
  } & NameProps
) => {
  interface Selection {
    selecting: boolean;
    sliding?: boolean;
    startPoint?: number;
    endPoint?: number;
  }
  const width = props.width;
  const leftPadding = INPUT_WIDTH + HANDLE_WIDTH / 2 + GAP_X;
  const rightPadding = HANDLE_WIDTH / 2;
  const innerWidth = width - leftPadding - rightPadding;

  const [defaultMin, defaultMax] = d3.extent(props.ticks) as [number, number];

  const [min, setMin] = React.useState<string | undefined>(
    props.selection?.[0]?.toString()
  );
  const [max, setMax] = React.useState<string | undefined>(
    props.selection?.[1]?.toString()
  );

  const scale = React.useMemo(
    () =>
      d3
        .scaleLinear()
        .domain(d3.extent(props.ticks) as [number, number])
        .range([0, innerWidth]),
    [props.ticks, innerWidth]
  );

  // Scale the given selection to the axis
  const propsStart = props.selection && scale(props.selection[0]);
  const propsEnd = props.selection && scale(props.selection[1]);

  const [{selecting, startPoint, endPoint}, setSelection] =
    React.useState<Selection>({
      selecting: false,
      startPoint: propsStart,
      endPoint: propsEnd,
    });

  // If the panel is resized, we need to reset the selection's location based on the new width/scale
  React.useEffect(() => {
    setSelection({
      selecting: false,
      startPoint: propsStart,
      endPoint: propsEnd,
    });
  }, [propsStart, propsEnd]);

  const [sliding, setSliding] = React.useState<boolean>(false);
  const [slidePos, setSlidePos] = React.useState<[number, number]>([0, 0]);

  let pos: [number, number] | null;

  // Pass two ranges of x values
  // This will scale the points to the domain, call the domain
  // setter and update locally if the setter makes a change
  const onSelect = (ps: [number, number]) => {
    const [p1, p2] = ps;

    const p1Scaled = scale.invert(p1);
    const p2Scaled = scale.invert(p2);
    const newScaledRange = props.onSelect([p1Scaled, p2Scaled]);
    setMin(newScaledRange[0].toString());
    setMax(newScaledRange[1].toString());
    const newSelection = newScaledRange.map(scale);

    setSelection({
      selecting: false,
      startPoint: newSelection[0],
      endPoint: newSelection[1],
    });
  };

  // Initial Selection Events
  const onStart = (e: React.MouseEvent<SVGElement>) => {
    if (pos == null || sliding) {
      return;
    }
    const startX = clamp(e.pageX - pos[0], {min: 0, max: innerWidth});
    setSelection({selecting: true, startPoint: startX});
  };

  const onDrag = (e: React.MouseEvent<SVGElement>) => {
    if (!selecting || pos == null) {
      return;
    }

    const endX = clamp(e.pageX - pos[0], {min: 0, max: innerWidth});
    setSelection(s => ({...s, endPoint: endX}));
  };

  const onEnd = (e: React.MouseEvent<SVGElement>) => {
    if (!selecting || pos == null) {
      return;
    }
    const endX = clamp(e.pageX - pos[0], {min: 0, max: innerWidth});
    setSelection(s => ({...s, endPoint: endX, selecting: false}));
    if (endX && startPoint) {
      onSelect([Math.min(endX, startPoint), Math.max(endX, startPoint)]);
    }
  };

  // Presented the selected part: the whole bar, the start handle or end handle
  type SlidePart = 'start-handle' | 'whole-bar' | 'end-handle';
  // Converts a part a single to its proper multishift(start and end)
  const partToShift = (shift: number, p: SlidePart): [number, number] =>
    p === 'start-handle'
      ? [shift, 0]
      : p === 'whole-bar'
        ? [shift, shift]
        : [0, shift];

  const onSelectionAdjust = (
    e: React.MouseEvent<SVGElement>,
    part: SlidePart
  ) => {
    if (startPoint != null && endPoint != null && !selecting) {
      const initial = [e.pageX, e.pageY] as [number, number];
      setSliding(true);
      e.stopPropagation();

      const onSlide = (se: MouseEvent) => {
        const diff = vec2.sub([se.pageX, se.pageY], initial);
        const shift = partToShift(diff[0], part);

        setSlidePos(shift);
      };

      document.body.addEventListener('mousemove', onSlide);

      const endSliding = (se: MouseEvent) => {
        const diff = vec2.sub([se.pageX, se.pageY], initial);
        const shift = partToShift(diff[0], part);
        setSlidePos([0, 0]);
        setSliding(false);
        const newPos = vec2.add(shift, [startPoint, endPoint]);
        const point = vec2.clamp(newPos, [0, width]);
        onSelect(point);

        // Cleanup
        document.body.removeEventListener('mouseup', endSliding);
        document.body.removeEventListener('mousemove', onSlide);
      };

      // Listen for next mouse up event anywhere
      document.body.addEventListener('mouseup', endSliding);
    }
  };

  const numberOfTicks = Math.floor(innerWidth / 100);

  const axis = React.useMemo(() => {
    const method =
      props.direction === 'top'
        ? d3.axisTop
        : props.direction === 'bottom'
          ? d3.axisBottom
          : props.direction === 'left'
            ? d3.axisLeft
            : d3.axisRight;

    const newAxis = method(scale);

    // if the width is too small to show all the ticks, show the ticks that fit
    if (numberOfTicks < props.ticks.length) {
      newAxis.ticks(numberOfTicks);
    } else {
      newAxis.tickValues(props.ticks);
    }
    newAxis.tickSize(8);
    return newAxis;
  }, [props.ticks, scale, props.direction, numberOfTicks]);

  const setRef = (ele: SVGElement | null) => {
    if (ele && axis) {
      axis(d3.select(ele as any));
      const bound = ele.getBoundingClientRect();
      pos = [bound.left, bound.top];
    }
  };

  // Display local state selection when in interactive mode
  // Otherwise the selection props are prioritized
  //
  // When neither are available terminate to null
  let selectionUI;
  // When a selecton has been made render UI
  if (startPoint != null && endPoint != null) {
    const [p1, p2] = vec2.clamp(vec2.add([startPoint, endPoint], slidePos), [
      0,
      innerWidth,
    ]);
    const width = Math.abs(p2 - p1);

    selectionUI = {
      x: Math.min(p1, p2),
      width,
    };
  }
  // Have to deconstruct for the dependencies not to depend on props
  const {onSelect: propsOnSelect} = props;

  const onInputRangeChange = useCallback(
    (newRange: [number, number]) => {
      const newScaledRange = propsOnSelect(newRange);
      const newSelectionRange = newScaledRange.map(scale);
      setSelection({
        selecting: false,
        startPoint: newSelectionRange[0],
        endPoint: newSelectionRange[1],
      });
      // sync the input values with the new range that's dependent on the existing steps
      setMin(newScaledRange[0].toString());
      setMax(newScaledRange[1].toString());
    },
    [propsOnSelect, scale, setSelection]
  );
  const debounceOnSelect = useMemo(
    () => _.debounce(onInputRangeChange, 500),
    [onInputRangeChange]
  );

  return (
    <div
      className="bg-white pb-32 pt-24"
      style={{
        width,
        height: HEIGHT,
      }}>
      <div className="flex h-full items-center overflow-x-auto">
        <SelectableAxisInput
          axisLabel={props.axisLabel}
          min={min}
          max={max}
          minPlaceholder={defaultMin.toString()}
          maxPlaceholder={defaultMax.toString()}
          setMin={(newMinString: string) => {
            setMin(newMinString);
            const newMin = parseInt(newMinString);
            if (Number.isFinite(newMin)) {
              debounceOnSelect([newMin, props.selection?.[1] ?? newMin]);
            }
          }}
          setMax={(newMaxString: string) => {
            setMax(newMaxString);
            const newMax = parseInt(newMaxString);
            if (Number.isFinite(newMax)) {
              debounceOnSelect([props.selection?.[0] ?? newMax, newMax]);
            }
          }}
          onClickReset={() => {
            props.onClear();
            setMin(undefined);
            setMax(undefined);
            setSelection({
              selecting: false,
              startPoint: propsStart,
              endPoint: propsEnd,
            });
          }}
        />
        {numberOfTicks >= MIN_NUMBER_OF_TICKS && (
          <svg
            style={{
              cursor: 'col-resize',
              userSelect: 'none',
              height: 50,
            }}
            width={width - INPUT_WIDTH}
            onMouseDown={onStart}
            onMouseUp={onEnd}
            onMouseMove={onDrag}>
            <g
              style={{
                fontSize: 15,
                fontFamily: 'Inconsolata',
                transform: `translateX(${
                  HANDLE_WIDTH / 2 + GAP_X
                }px) translateY(${
                  HANDLE_HEIGHT + SCALE_THICKNESS - HANDLE_Y_OFFSET
                }px)`,
              }}
              ref={setRef}
            />
            {selectionUI && (
              <g
                style={{
                  transform: `translateX(${HANDLE_WIDTH / 2 + GAP_X}px)`,
                }}>
                <rect
                  style={{cursor: 'grab'}}
                  onMouseDown={e => onSelectionAdjust(e, 'whole-bar')}
                  width={selectionUI.width}
                  height={SCALE_THICKNESS}
                  fill={globals.TEAL_450}
                  color="black"
                  x={selectionUI.x}
                  y={HANDLE_HEIGHT - HANDLE_Y_OFFSET}
                />
                <g className="axis-control" width="40" height="40">
                  <rect
                    className="axis-control__stick"
                    onMouseDown={e => onSelectionAdjust(e, 'start-handle')}
                    x={selectionUI.x - HANDLE_SIZE / 2}
                    y={HANDLE_HEIGHT - HANDLE_Y_OFFSET}
                    width={HANDLE_SIZE}
                    height={SCALE_THICKNESS}
                  />
                  <image
                    onMouseDown={e => onSelectionAdjust(e, 'start-handle')}
                    href={stepFilterHandleUrl}
                    x={selectionUI.x - HANDLE_WIDTH / 2}
                    y={0}
                    width={HANDLE_WIDTH}
                    height={HANDLE_HEIGHT}
                  />
                </g>
                <g className="axis-control" width="20" height="40">
                  <rect
                    onMouseDown={e => onSelectionAdjust(e, 'end-handle')}
                    className="axis-control__stick"
                    x={selectionUI.x + selectionUI.width - HANDLE_SIZE / 2}
                    y={HANDLE_HEIGHT - HANDLE_Y_OFFSET}
                    width={HANDLE_SIZE}
                    height={SCALE_THICKNESS}
                  />
                  <image
                    onMouseDown={e => onSelectionAdjust(e, 'end-handle')}
                    href={stepFilterHandleUrl}
                    x={selectionUI.x + selectionUI.width - HANDLE_WIDTH / 2}
                    y={0}
                    width={HANDLE_WIDTH}
                    height={HANDLE_HEIGHT}
                  />
                </g>
              </g>
            )}
          </svg>
        )}
      </div>
    </div>
  );
};

// Note I originally wrote this to support X, and Y Axises
// but decided to narrow the scope to just the X axis for time being
export const StaticAxis = (
  props: {
    direction: 'top' | 'bottom' | 'left' | 'right';
    width: number;
    // A range of numbers(inclusive) that represents the current selection
    ticks: number[];
    // The size of the in the axis
    itemWidth?: number;
  } & NameProps
) => {
  const itemWidth = props.itemWidth || 0;
  const padding = itemWidth / 2;
  const innerWidth = props.width - 2 * padding;

  const scale = React.useMemo(
    () =>
      d3
        .scaleLinear()
        .domain(d3.extent(props.ticks) as [number, number])
        .range([0, innerWidth]),
    [props.ticks, innerWidth]
  );

  const axis = React.useMemo(() => {
    const method =
      props.direction === 'top'
        ? d3.axisTop
        : props.direction === 'bottom'
          ? d3.axisBottom
          : props.direction === 'left'
            ? d3.axisLeft
            : d3.axisRight;

    const newAxis = method(scale);

    // Use fixed scale values when given
    if (props.ticks) {
      newAxis.tickValues(props.ticks);
    }
    return newAxis;
  }, [props.ticks, scale, props.direction]);

  const setRef = (ele: SVGElement | null) => {
    if (ele && axis) {
      axis(d3.select(ele as any));
    }
  };

  return (
    <svg {...pickNameProps(props)} width={props.width} height={15}>
      <g
        style={{transform: `translateX(${padding}px) translateY(20px)`}}
        ref={setRef}
      />
    </svg>
  );
};
